import yaml from "js-yaml";
import { buildSchema, parse as parseGraphQL } from "graphql";

/**
 * Parse OpenAPI (Swagger) schema and extract endpoints
 */
export const parseOpenAPISchema = (content, isYaml = false) => {
  try {
    const schema = isYaml ? yaml.load(content) : JSON.parse(content);
    const endpoints = [];

    if (!schema.paths) {
      throw new Error("Invalid OpenAPI schema: missing 'paths'");
    }

    // Extract API info
    const apiInfo = {
      title: schema.info?.title || "Untitled API",
      version: schema.info?.version || "1.0.0",
      description: schema.info?.description || "",
      baseUrl: schema.servers?.[0]?.url || "",
    };

    // Parse paths
    Object.entries(schema.paths).forEach(([path, methods]) => {
      Object.entries(methods).forEach(([method, details]) => {
        if (["get", "post", "put", "patch", "delete"].includes(method)) {
          const endpoint = {
            id: `${method}-${path}`.replace(/[^a-zA-Z0-9]/g, "-"),
            name: details.operationId || details.summary || `${method.toUpperCase()} ${path}`,
            path,
            method: method.toUpperCase(),
            summary: details.summary || "",
            description: details.description || "",
            tags: details.tags || [],
            parameters: parseParameters(details.parameters || []),
            requestBody: parseRequestBody(details.requestBody, schema),
            responses: parseResponses(details.responses || {}, schema),
            supportedMethods: method.toUpperCase(),
            supportedActions: ["Test", "Mock"],
          };
          endpoints.push(endpoint);
        }
      });
    });

    return {
      type: "openapi",
      info: apiInfo,
      endpoints,
      rawSchema: schema,
    };
  } catch (error) {
    throw new Error(`Failed to parse OpenAPI schema: ${error.message}`);
  }
};

/**
 * Parse GraphQL SDL and extract queries/mutations
 */
export const parseGraphQLSchema = (sdl) => {
  try {
    const schema = buildSchema(sdl);
    const endpoints = [];

    // Get Query type
    const queryType = schema.getQueryType();
    if (queryType) {
      const queryFields = queryType.getFields();
      Object.entries(queryFields).forEach(([name, field]) => {
        endpoints.push({
          id: `query-${name}`,
          name: name,
          path: `/${name}`,
          method: "QUERY",
          type: "query",
          description: field.description || "",
          args: parseGraphQLArgs(field.args),
          returnType: field.type.toString(),
          supportedMethods: "QUERY",
          supportedActions: ["Test", "Mock"],
        });
      });
    }

    // Get Mutation type
    const mutationType = schema.getMutationType();
    if (mutationType) {
      const mutationFields = mutationType.getFields();
      Object.entries(mutationFields).forEach(([name, field]) => {
        endpoints.push({
          id: `mutation-${name}`,
          name: name,
          path: `/${name}`,
          method: "MUTATION",
          type: "mutation",
          description: field.description || "",
          args: parseGraphQLArgs(field.args),
          returnType: field.type.toString(),
          supportedMethods: "MUTATION",
          supportedActions: ["Test", "Mock"],
        });
      });
    }

    // Extract types for reference
    const types = {};
    const typeMap = schema.getTypeMap();
    Object.entries(typeMap).forEach(([typeName, type]) => {
      if (!typeName.startsWith("__") && type.getFields) {
        const fields = type.getFields();
        types[typeName] = Object.entries(fields).map(([fieldName, field]) => ({
          name: fieldName,
          type: field.type.toString(),
          description: field.description || "",
        }));
      }
    });

    return {
      type: "graphql",
      info: {
        title: "GraphQL API",
        version: "1.0.0",
      },
      endpoints,
      types,
      rawSchema: sdl,
    };
  } catch (error) {
    throw new Error(`Failed to parse GraphQL schema: ${error.message}`);
  }
};

/**
 * Detect schema type from content
 */
export const detectSchemaType = (content) => {
  const trimmed = content.trim();

  // Check for GraphQL SDL patterns
  if (
    trimmed.includes("type Query") ||
    trimmed.includes("type Mutation") ||
    trimmed.includes("schema {") ||
    /^(type|interface|enum|input|scalar|union)\s+\w+/.test(trimmed)
  ) {
    return "graphql";
  }

  // Check for OpenAPI/Swagger patterns
  try {
    const parsed = trimmed.startsWith("{") ? JSON.parse(trimmed) : yaml.load(trimmed);
    if (parsed.openapi || parsed.swagger || parsed.paths) {
      return trimmed.startsWith("{") ? "openapi-json" : "openapi-yaml";
    }
  } catch (e) {
    // Not valid JSON or YAML
  }

  return "unknown";
};

/**
 * Auto-parse schema based on detected type
 */
export const parseSchema = (content) => {
  const schemaType = detectSchemaType(content);

  switch (schemaType) {
    case "graphql":
      return parseGraphQLSchema(content);
    case "openapi-json":
      return parseOpenAPISchema(content, false);
    case "openapi-yaml":
      return parseOpenAPISchema(content, true);
    default:
      throw new Error("Unable to detect schema type. Please ensure it's valid OpenAPI or GraphQL SDL.");
  }
};

// Helper functions

const parseParameters = (parameters) => {
  return parameters.map((param) => ({
    name: param.name,
    in: param.in,
    required: param.required || false,
    type: param.schema?.type || "string",
    description: param.description || "",
  }));
};

const parseRequestBody = (requestBody, schema) => {
  if (!requestBody) return null;

  const content = requestBody.content || {};
  const jsonContent = content["application/json"];

  if (jsonContent?.schema) {
    return {
      required: requestBody.required || false,
      contentType: "application/json",
      schema: resolveSchema(jsonContent.schema, schema),
    };
  }

  return null;
};

const parseResponses = (responses, schema) => {
  const parsed = {};

  Object.entries(responses).forEach(([code, response]) => {
    const content = response.content || {};
    const jsonContent = content["application/json"];

    parsed[code] = {
      description: response.description || "",
      schema: jsonContent?.schema ? resolveSchema(jsonContent.schema, schema) : null,
    };
  });

  return parsed;
};

const resolveSchema = (schemaRef, fullSchema) => {
  if (schemaRef.$ref) {
    const refPath = schemaRef.$ref.replace("#/", "").split("/");
    let resolved = fullSchema;
    for (const key of refPath) {
      resolved = resolved?.[key];
    }
    return resolved || schemaRef;
  }
  return schemaRef;
};

const parseGraphQLArgs = (args) => {
  return args.map((arg) => ({
    name: arg.name,
    type: arg.type.toString(),
    description: arg.description || "",
    defaultValue: arg.defaultValue,
  }));
};

/**
 * Generate mock data from schema
 */
export const generateMockFromSchema = (schema) => {
  if (!schema) return {};

  if (schema.type === "object" && schema.properties) {
    const mock = {};
    Object.entries(schema.properties).forEach(([key, prop]) => {
      mock[key] = generateMockValue(prop);
    });
    return mock;
  }

  if (schema.type === "array" && schema.items) {
    return [generateMockFromSchema(schema.items)];
  }

  return generateMockValue(schema);
};

const generateMockValue = (prop) => {
  switch (prop.type) {
    case "string":
      if (prop.format === "email") return "user@example.com";
      if (prop.format === "date") return "2024-01-01";
      if (prop.format === "date-time") return "2024-01-01T00:00:00Z";
      if (prop.format === "uuid") return "550e8400-e29b-41d4-a716-446655440000";
      return prop.example || "string";
    case "integer":
    case "number":
      return prop.example || 0;
    case "boolean":
      return prop.example || false;
    case "array":
      return prop.items ? [generateMockValue(prop.items)] : [];
    case "object":
      return generateMockFromSchema(prop);
    default:
      return null;
  }
};
